/**
 * +field: solo grind mode (docs/design/zones.md "Auto"). On an open field the bot plays alone:
 *   - fights whatever is engaged with it first (never an evading mob);
 *   - otherwise hunts the nearest suitable mob: not evading, not 4+ levels above the hero, not
 *     standing in or reached through a portal, elites only when healthy; grey mobs cost extra;
 *   - every role pulls: melee walk in, ranged cast from range (the pull press must hit the target);
 *   - rests out of combat below ~45% HP (or low mana) until nearly full (the sim regenerates);
 *   - picks up ground loot nearby (gold, XP wisps, gear) and opens a treasure chest when the coast is
 *     clear; the fleeing treasure sprite is left to active players;
 *   - with nothing in reach, walks between suitable spawn points;
 *   - never walks into or paths through a portal, never leaves the field. Towns: do nothing.
 *   - +zone: on a big map Auto keeps to a home (where it was turned on: a radius, and the region it
 *     stands in): it hunts and roams to camps there, never across the zone, and never in a safe
 *     area (town).
 * The dungeon brain (movement.ts) is untouched: brain.ts only routes here when grinding.
 */
import type { Command, DeepReadonly, FieldDef, FieldSpawnDef, Ms, UnitId, Vec2 } from '@keystone/sim';
import { fieldAreaAt, fieldRegionAt } from '@keystone/sim';
import { add, dist, norm, scale, sub } from './geometry';
import type { ClassKit } from './kits';
import type { BotMemory } from './memory';
import { abilityLocked, hpPct, inMelee, meleeRange, resourcePct, simWorld, type DangerArea, type Perception, type RUnit } from './perception';
import { inDanger, safePoint } from './awareness/dodge';
import { type GatherMemory, channelling, createGatherMemory, nextGatherNode, pickGatherNode, planGather } from './gather'; // +prof

/** What Auto is doing (HUD label "AUTO: hunting / resting"). */
export type GrindState = 'idle' | 'town' | 'dead' | 'resting' | 'fighting' | 'hunting' | 'roaming' | 'looting' | 'gathering'; // +prof: gathering

export interface GrindStatus {
  state: GrindState;
  /** The mob being hunted or fought. */
  targetId: UnitId | null;
}

/** Rest out of combat below this HP fraction ... */
export const GRIND_REST_HP = 0.45;
/** ... until back above this one. */
export const GRIND_RESTED_HP = 0.95;
/** Mana users also rest below this mana fraction (until GRIND_RESTED_MANA). */
export const GRIND_REST_MANA = 0.25;
export const GRIND_RESTED_MANA = 0.85;

/** Mobs this many levels above the hero (or more) are never pulled. */
export const GRIND_MAX_LEVEL_GAP = 4;
/**
 * +basekits: mobs this many levels below the hero (or more) are not hunted (≤ 40% XP); the bot
 * roams on to spawn points near its level instead. Engaged ones are still fought.
 */
export const GRIND_GREY_LEVEL_GAP = 4;
/** Extra clearance kept around portals (yards). */
export const PORTAL_MARGIN = 4;
/** Ranged heroes open from about this far (centre distance). */
const RANGED_PULL_YD = 24;
/** Hunt only within this distance; farther mobs are reached by roaming between spawn points. */
const HUNT_RADIUS = 60;
/** A target found unreachable / through a portal is ignored for this long. */
const AVOID_MS: Ms = 20_000;
/** A spawn point just visited is skipped for this long while roaming. */
const VISIT_TTL_MS: Ms = 40_000;
/** Resting keeps this far from every living mob (aggro radius 9 + wander). */
const REST_CLEAR_YD = 16;
const REST_STEP_YD = 10;
/** Elites only above this HP fraction. */
const ELITE_HP = 0.9;
/** Ground loot within this distance beats the next hunt; chests within LOOT_CHEST_YD when no mob is near. */
const LOOT_NEAR_YD = 14;
const LOOT_CHEST_YD = 30;
const CHEST_CLEAR_YD = 10;

/** +zone: Auto's hunting ground on a big map: within `radius` of `pos`, or anywhere in region `region` within 2 × radius. */
export interface GrindHome {
  pos: Vec2;
  radius: number;
  region?: string | null;
}

export interface GrindMemory {
  /** +zone: where this bot hunts (null = the whole map). */
  home?: GrindHome | null;
  status: GrindStatus;
  resting: boolean;
  huntId: UnitId | null;
  /** Units to leave alone until a time (unreachable, path through a portal). */
  avoid: Map<UnitId, Ms>;
  /** Spawn id → when it was last visited while roaming. */
  visited: Map<string, Ms>;
  roamTo: string | null;
  /** Ground loot / chest to walk to (null = none). */
  lootAt: Vec2 | null;
  /** +prof: the Auto setting (Hunt / Gather / Both) and the node being gathered. */
  gather: GatherMemory;
  /** +grid: Auto's wits: rest below GRIND_REST_HP + this (Waystone Grid). */
  restBias?: number;
}

export function createGrindMemory(): GrindMemory {
  return { status: { state: 'idle', targetId: null }, resting: false, huntId: null, avoid: new Map(), visited: new Map(), roamTo: null, lootAt: null, gather: createGatherMemory() }; // +prof: gather
}

type RField = DeepReadonly<FieldDef>;

function fieldOf(p: Perception): RField | undefined {
  return p.dungeon?.field as RField | undefined;
}

/** Inside a portal's trigger radius plus margin. */
export function nearPortal(field: RField | undefined, at: Vec2, extra = PORTAL_MARGIN): boolean {
  if (!field) return false;
  return field.portals.some((pt) => dist(pt.pos, at) <= pt.radius + extra);
}

/** Push a point out of every portal's radius + margin (keeps walking targets clear of portals). */
export function awayFromPortals(field: RField | undefined, at: Vec2): Vec2 {
  if (!field) return at;
  let q = at;
  for (const pt of field.portals) {
    const need = pt.radius + PORTAL_MARGIN + 0.5;
    const d = dist(pt.pos, q);
    if (d < need) q = add(pt.pos, scale(norm(sub(q, pt.pos)), need));
  }
  return q;
}

/** Does the walk from `from` to `to` pass through a portal trigger (radius + 1 yd)? */
/**
 * +zone: the walk to `to` is a long way round (the other side of a cliff or the river): the path is
 * more than twice the straight line and over 40 yd longer. Auto leaves such targets for later.
 */
function roundabout(p: Perception, from: Vec2, to: Vec2): boolean {
  const straight = dist(from, to);
  if (straight < 12) return false;
  const path = p.view.queries.spatial.findPath(simWorld(p), from, to);
  if (!path) return true;
  let len = 0;
  let at = from;
  for (const wp of path) {
    len += dist(at, wp);
    at = wp;
  }
  return len > straight * 2 && len - straight > 40;
}

function pathCrossesPortal(p: Perception, field: RField | undefined, from: Vec2, to: Vec2): boolean {
  if (!field || field.portals.length === 0) return false;
  const path = p.view.queries.spatial.findPath(simWorld(p), from, to) ?? [to];
  let at = from;
  // Knocked into a portal mouth (a boar's charge): walking out of it is fine, re-entering is not.
  let leaving = nearPortal(field, from, 1);
  for (const wp of path) {
    const n = Math.max(1, Math.ceil(dist(at, wp)));
    for (let k = 1; k <= n; k++) {
      const q = { x: at.x + ((wp.x - at.x) * k) / n, y: at.y + ((wp.y - at.y) * k) / n };
      const inside = nearPortal(field, q, 1);
      if (leaving) {
        if (!inside) leaving = false;
        continue;
      }
      if (inside) return true;
    }
    at = wp;
  }
  return false;
}

function needsRest(p: Perception, g: GrindMemory): boolean {
  const self = p.self;
  const manaUser = (self.resources.mana?.max ?? 0) > 0;
  const hp = hpPct(self);
  const mana = manaUser ? resourcePct(self, 'mana') : 1;
  if (g.resting) return hp < GRIND_RESTED_HP || mana < GRIND_RESTED_MANA;
  return hp < GRIND_REST_HP + (g.restBias ?? 0) || mana < GRIND_REST_MANA;
}

/** Level of a mob for grind decisions (unlevelled = the hero's own). */
function levelGap(self: RUnit, e: RUnit): number {
  if (self.level === undefined || e.level === undefined) return 0;
  return e.level - self.level;
}

/** XP-efficiency penalty in yards: the lower the mob below the hero, the less it is worth the walk. */
function gapPenalty(gap: number): number {
  if (gap <= -5) return 45;
  if (gap <= -3) return 25;
  if (gap === -2) return 14;
  if (gap === -1) return 6;
  return 0;
}

/**
 * The mob this bot should be hitting: an engaged one first (current target, else nearest), else
 * the hunt target. Updates `g.status` and `g.resting`.
 */
/**
 * +mage-specs: engaged enemies worth fighting. The Tower Warden's practice dummy is never one: stray
 * AoE (a drifting Wandering Glacier, Deep Winter) can tag it, and it would hold Auto forever.
 */
function fightable(p: Perception): RUnit[] {
  return p.engaged.filter((e) => !(e.npcId && p.view.content.npc(e.npcId)?.practice));
}

export function grindTarget(p: Perception, g: GrindMemory): RUnit | undefined {
  const self = p.self;
  const field = fieldOf(p);
  // +zone: on a map with areas Auto always has a home; without one set, it is where the bot started.
  if (g.home === undefined && field?.areas?.length) g.home = { pos: { x: self.pos.x, y: self.pos.y }, radius: 55, region: fieldRegionAt(field as FieldDef, self.pos)?.id ?? null };
  for (const [id, until] of g.avoid) if (until <= p.now) g.avoid.delete(id);
  const engaged = fightable(p).filter((e) => e.ai?.mode !== 'evading');
  if (engaged.length > 0) {
    g.resting = false;
    const cur = self.targetId ? engaged.find((e) => e.id === self.targetId) : undefined;
    // Kill the one hitting me that dies fastest when the current target is gone.
    const t = cur ?? engaged.reduce((b, e) => (dist(e.pos, self.pos) - (e.targetId === self.id ? 5 : 0) < dist(b.pos, self.pos) - (b.targetId === self.id ? 5 : 0) ? e : b));
    g.huntId = t.id;
    g.status = { state: 'fighting', targetId: t.id };
    return t;
  }
  g.resting = needsRest(p, g);
  if (g.resting) {
    g.huntId = null;
    g.status = { state: 'resting', targetId: null };
    return undefined;
  }
  // Loot lying close by first (it would expire, and it's quick).
  g.lootAt = lootSpot(p);
  if (g.lootAt) {
    g.status = { state: 'looting', targetId: null };
    return undefined;
  }
  // +prof: Auto Gather / Both: a node in reach beats the next hunt; Gather never hunts.
  if (g.gather.mode !== 'hunt') {
    const node = channelling(p) || pickGatherNode(p, g.gather, field, (at) => inHome(g, field, at, 4), (at) => nearPortal(field, at));
    if (node) {
      g.huntId = null;
      g.status = { state: 'gathering', targetId: null };
      return undefined;
    }
    if (g.gather.mode === 'gather') {
      g.huntId = null;
      g.status = { state: 'roaming', targetId: null };
      return undefined;
    }
  }
  const keep = g.huntId ? p.world.units[g.huntId] : undefined;
  if (keep && huntable(p, g, field, keep)) {
    g.status = { state: 'hunting', targetId: keep.id };
    return keep;
  }
  g.huntId = null;
  let best: RUnit | undefined;
  let bestScore = Infinity;
  for (const e of p.enemies) {
    if (!huntable(p, g, field, e)) continue;
    const d = dist(e.pos, self.pos);
    if (d > HUNT_RADIUS) continue;
    // Other mobs close to it would join (bodies within its aggro reach): a little extra.
    let adds = 0;
    for (const o of p.enemies) if (o.id !== e.id && dist(o.pos, e.pos) < 8) adds++;
    const score = d + gapPenalty(levelGap(self, e)) + (e.elite ? 25 : 0) + adds * 6;
    if (score < bestScore) {
      bestScore = score;
      best = e;
    }
  }
  if (best && (pathCrossesPortal(p, field, self.pos, best.pos) || roundabout(p, self.pos, best.pos))) {
    g.avoid.set(best.id, p.now + AVOID_MS);
    best = undefined;
  }
  g.huntId = best?.id ?? null;
  g.status = best ? { state: 'hunting', targetId: best.id } : { state: 'roaming', targetId: null };
  return best;
}

/** +zone: inside the bot's hunting ground (and not in a safe area). */
function inHome(g: GrindMemory, field: RField | undefined, at: Vec2, slack = 0): boolean {
  if (field?.areas?.length && fieldAreaAt(field as FieldDef, at)?.safe) return false;
  const h = g.home;
  if (!h) return true;
  const d = dist(h.pos, at);
  if (d <= h.radius + slack) return true;
  return !!h.region && d <= h.radius * 2 + slack && fieldRegionAt(field as FieldDef, at)?.id === h.region;
}

function huntable(p: Perception, g: GrindMemory, field: RField | undefined, e: RUnit): boolean {
  if (!inHome(g, field, e.pos, 8)) return false;
  if (!e.alive || e.faction !== 'enemy' || e.ai?.mode === 'evading' || e.ai?.mode === 'dead') return false;
  // The treasure sprite runs; chasing it is for active players.
  if (e.npcId && p.view.content.npc(e.npcId)?.flees) return false;
  if (e.npcId && p.view.content.npc(e.npcId)?.practice) return false; // +specs: the Tower Warden's dummy
  if (g.avoid.has(e.id)) return false;
  if (levelGap(p.self, e) >= GRIND_MAX_LEVEL_GAP) return false;
  if (levelGap(p.self, e) <= -GRIND_GREY_LEVEL_GAP) return false; // +basekits
  if (e.elite && hpPct(p.self) < ELITE_HP) return false;
  if (nearPortal(field, e.pos)) return false;
  return p.view.queries.spatial.isWalkable(simWorld(p), e.pos);
}

/** +basekits: kite a rooted target from inside this distance out to KITE_TO_YD. */
const KITE_FROM_YD = 8;
const KITE_TO_YD = 17;

/** The unit can't move (rooted) but can still act: worth stepping away from. */
function rootedOnly(p: Perception, u: RUnit): boolean {
  return u.auras.some((a) => p.view.content.aura(a.auraId)?.cc === 'root') && !!p.view.content.npc(u.npcId ?? '')?.melee && !p.view.content.npc(u.npcId ?? '')?.preferredRange;
}

/** Walk / stand for the grind. */
export function planGrindPosition(p: Perception, kit: ClassKit, mem: BotMemory, g: GrindMemory, target: RUnit | undefined, dangers: readonly DangerArea[]): Command[] {
  const self = p.self;
  if (!target) {
    const fights = fightable(p).length; // +mage-specs: not the practice dummy
    if (g.lootAt && !g.resting && fights === 0) return grindMove(p, mem, g.lootAt, dangers) ?? stand(p, mem);
    if (g.resting && fights === 0) return restSpot(p, mem, dangers);
    if (g.resting || fights > 0) return stand(p, mem);
    // +prof: walk to the node and channel it; Gather mode waits by the node that is back soonest.
    if (g.status.state === 'gathering' || g.gather.mode === 'gather') {
      const field = fieldOf(p);
      const id = g.gather.target;
      const node = (id ? field?.gatherNodes?.find((n) => n.id === id) : undefined) ?? (g.status.state === 'gathering' ? undefined : nextGatherNode(p, field, (at) => inHome(g, field, at, 4)));
      if (channelling(p)) return [];
      if (node && g.status.state === 'gathering') return planGather(p, mem, g.gather, node, (to) => grindMove(p, mem, to, dangers));
      if (node) return dist(node.pos, p.self.pos) > 4 ? (grindMove(p, mem, node.pos, dangers) ?? stand(p, mem)) : stand(p, mem);
      if (g.gather.mode === 'gather') return stand(p, mem);
    }
    return roam(p, mem, g, dangers);
  }
  const engagedT = p.engaged.includes(target);
  const d = dist(self.pos, target.pos);
  let dest: Vec2 | null = null;
  // +basekits: a ranged hero steps out of reach of a rooted melee target (Hoarbind, Snare Coil),
  // blinking away first when it can (Fold Step).
  if (kit.ranged && engagedT && d < KITE_FROM_YD && rootedOnly(p, target)) {
    const away = norm(sub(self.pos, target.pos));
    const to = add(target.pos, scale(away, KITE_TO_YD));
    const cmds: Command[] = [];
    if (kit.blink && !abilityLocked(p.view, self, kit.blink) && p.view.queries.canCast(self.id, kit.blink, null, null).ok) {
      cmds.push({ type: 'face', actorId: self.id, facing: Math.atan2(away.y, away.x) }, { type: 'cast', actorId: self.id, abilityId: kit.blink });
    }
    const mv = grindMove(p, mem, to, dangers);
    if (mv) return [...cmds, ...mv];
  }
  if (kit.ranged) {
    if (d > RANGED_PULL_YD) dest = add(target.pos, scale(norm(sub(self.pos, target.pos)), RANGED_PULL_YD - 4));
  } else if (!inMelee(self, target)) {
    dest = add(target.pos, scale(norm(sub(self.pos, target.pos)), meleeRange(self, target) * 0.6));
  }
  if (!dest) return hold(mem);
  const cmds = grindMove(p, mem, dest, dangers);
  if (cmds === null) {
    // Unreachable without touching a portal: drop a hunt target; an engaged one comes to us.
    if (!engagedT) {
      g.avoid.set(target.id, p.now + AVOID_MS);
      g.huntId = null;
    }
    return stand(p, mem);
  }
  return cmds;
}

/**
 * Rest clear of aggro: step away from mobs, and from spawn points (a respawn on top of a resting
 * hero aggroes at once), before sitting down.
 */
function restSpot(p: Perception, mem: BotMemory, dangers: readonly DangerArea[]): Command[] {
  const self = p.self;
  const field = fieldOf(p);
  const threats: { pos: Vec2; clear: number }[] = [
    ...p.enemies.map((e) => ({ pos: e.pos, clear: REST_CLEAR_YD })),
    ...(field?.spawns ?? []).map((s) => ({ pos: s.pos, clear: s.radius + REST_CLEAR_YD - 4 })),
  ];
  const clearAt = (q: Vec2, slack = 0): boolean => threats.every((t) => dist(t.pos, q) >= t.clear - slack);
  if (clearAt(self.pos, 2)) return self.move ? stand(p, mem) : hold(mem);
  let away = { x: 0, y: 0 };
  for (const t of threats) {
    const d = dist(self.pos, t.pos);
    if (d < t.clear) away = add(away, scale(norm(sub(self.pos, t.pos)), (t.clear - d) / t.clear));
  }
  const base = Math.atan2(away.y, away.x);
  let best: Vec2 | null = null;
  for (const step of [REST_STEP_YD, REST_STEP_YD * 2]) {
    for (const turn of [0, 0.5, -0.5, 1, -1, 1.6, -1.6, 2.2, -2.2, Math.PI]) {
      const a = base + turn;
      const q = { x: self.pos.x + Math.cos(a) * step, y: self.pos.y + Math.sin(a) * step };
      if (!p.view.queries.spatial.isWalkable(simWorld(p), q) || nearPortal(field, q)) continue;
      if (clearAt(q)) {
        best = q;
        break;
      }
    }
    if (best) break;
  }
  if (best) {
    const cmds = grindMove(p, mem, best, dangers);
    if (cmds) return cmds;
  }
  return self.move ? [] : hold(mem); // boxed in: finish the current step, else rest here
}

/** Nothing in reach: walk to the nearest suitable spawn point not visited recently. */
function roam(p: Perception, mem: BotMemory, g: GrindMemory, dangers: readonly DangerArea[]): Command[] {
  const field = fieldOf(p);
  if (!field) return stand(p, mem);
  const self = p.self;
  const lvl = self.level;
  for (const [id, t] of g.visited) if (p.now - t > VISIT_TTL_MS) g.visited.delete(id);
  const cur = g.roamTo ? field.spawns.find((s) => s.id === g.roamTo) : undefined;
  if (cur && dist(self.pos, cur.pos) <= Math.max(3, cur.radius * 0.5)) {
    g.visited.set(cur.id, p.now);
    g.roamTo = null;
  }
  let to = g.roamTo ? field.spawns.find((s) => s.id === g.roamTo) : undefined;
  if (!to) {
    let bestScore = Infinity;
    // +basekits: spawn points near the hero's level first; any suitable one when none is left.
    for (const greyOk of [false, true]) {
      for (const s of field.spawns) {
        if (g.visited.has(s.id) || !spawnSuits(s, lvl, hpPct(self)) || nearPortal(field, s.pos) || !inHome(g, field, s.pos)) continue;
        if (!greyOk && lvl !== undefined && (s.level[1] ?? 0) - lvl <= -GRIND_GREY_LEVEL_GAP) continue;
        const score = dist(s.pos, self.pos) + (lvl !== undefined ? gapPenalty((s.level[1] ?? 0) - lvl) : 0);
        if (score < bestScore) {
          bestScore = score;
          to = s;
        }
      }
      if (to) break;
    }
    // +zone: a spawn point the long way round (below a cliff, across the river) waits its turn.
    if (to && roundabout(p, self.pos, to.pos)) {
      g.visited.set(to.id, p.now);
      return stand(p, mem);
    }
    // Everything visited recently: start the round again.
    if (!to && g.visited.size > 0) {
      g.visited.clear();
      return stand(p, mem);
    }
    g.roamTo = to?.id ?? null;
  }
  if (!to) return stand(p, mem);
  const cmds = grindMove(p, mem, to.pos, dangers);
  if (cmds === null) {
    g.visited.set(to.id, p.now);
    g.roamTo = null;
    return stand(p, mem);
  }
  return cmds;
}

function spawnSuits(s: DeepReadonly<FieldSpawnDef>, lvl: number | undefined, hp: number): boolean {
  if (s.elite && hp < ELITE_HP) return false;
  return lvl === undefined || (s.level[0] ?? 0) - lvl < GRIND_MAX_LEVEL_GAP;
}

/** Move order toward `dest` (kept off portals, on walkable ground, out of dangers); null = no safe path. */
function grindMove(p: Perception, mem: BotMemory, dest: Vec2, dangers: readonly DangerArea[]): Command[] | null {
  const field = fieldOf(p);
  const self = p.self;
  let to = p.view.queries.spatial.clampToWalkable(simWorld(p), awayFromPortals(field, dest));
  if (nearPortal(field, to)) return null;
  if (inDanger(p, dangers, to, self.radius)) {
    if (inDanger(p, dangers, self.pos, self.radius)) return [];
    const alt = safePoint(p, dangers, to, self.pos);
    if (!alt) return [];
    to = alt;
  }
  const last = mem.lastMoveDest;
  if (last && self.move && dist(last, to) < 1.5) return [];
  if (pathCrossesPortal(p, field, self.pos, to)) return null;
  mem.lastMoveDest = to;
  return [{ type: 'move', actorId: self.id, dest: to }];
}

function hold(mem: BotMemory): Command[] {
  mem.lastMoveDest = null;
  return [];
}

/** Stop walking (resting, or nowhere to go). */
function stand(p: Perception, mem: BotMemory): Command[] {
  mem.lastMoveDest = null;
  return p.self.move ? [{ type: 'stop', actorId: p.self.id }] : [];
}

/**
 * The nearest loot worth a detour: a ground pickup within LOOT_NEAR_YD (only the looter can take
 * them), else an unopened treasure chest within LOOT_CHEST_YD with no living mob next to it.
 */
function lootSpot(p: Perception): Vec2 | null {
  const w = simWorld(p);
  const self = p.self;
  if (w.worldLoot?.looterId !== self.id) return null;
  const field = fieldOf(p);
  let best: Vec2 | null = null;
  let bd = LOOT_NEAR_YD;
  for (const q of w.pickups ?? []) {
    if (p.now < q.landAt) continue;
    const d = dist(q.pos, self.pos);
    if (d < bd && !nearPortal(field, q.pos)) {
      bd = d;
      best = q.pos;
    }
  }
  if (best) return { x: best.x, y: best.y };
  for (const o of w.worldObjects ?? []) {
    if (o.kind !== 'treasure_chest' || o.broken || o.z !== undefined) continue; // +glide: sky chests are for gliders, never Auto
    if (dist(o.pos, self.pos) > LOOT_CHEST_YD || nearPortal(field, o.pos)) continue;
    if (p.enemies.some((e) => e.alive && dist(e.pos, o.pos) < CHEST_CLEAR_YD)) continue;
    return { x: o.pos.x, y: o.pos.y };
  }
  return null;
}

/** Grind healers triage only when they need it (self below 60%, or resting). */
export function grindWantsHeal(p: Perception, g: GrindMemory): boolean {
  return g.resting || hpPct(p.self) < 0.6;
}
